import { useEffect, useMemo, useState } from 'react';
import { execApi } from '../../../services/api';
import { today } from '../../../utils/date';

// Point-in-time stock by lifecycle stage (פרי / חדש / פעיל / סיים) AS OF a chosen date.
// Cards = count per stage; bars below = each stage split by clinic (קופה).

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const STAGE_COLOR: Record<string, string> = { pre: '#F59E0B', new: '#2563EB', active: '#10B981', done: '#8B5CF6' };
const CLINIC_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#0D9488', '#DB2777', '#64748B'];
const fmt = (n: number) => n.toLocaleString();
const todayISO = today;

type Stage = { key: string; label: string; count: number; pct: number };
type Cl = { stage: string; clinic: string; count: number };

export function StageSnapshot() {
  const dateOpts = useMemo(() => {
    const out: { id: string; label: string }[] = [{ id: todayISO(), label: 'היום' }];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      if (end >= now) continue;
      out.push({ id: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`, label: `סוף ${HE_MONTHS[end.getMonth()]} ${end.getFullYear()}` });
    }
    return out;
  }, []);

  const [asOf, setAsOf] = useState(dateOpts[0].id);
  const [clinicFilter, setClinicFilter] = useState('');
  const [selStage, setSelStage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    execApi.getStageSnapshot(asOf)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [asOf]);

  const allStages: Stage[] = data?.stages ?? [];
  const byClinic: Cl[] = data?.by_clinic ?? [];
  const clinics: string[] = data?.clinics ?? [];
  const clinicColor = (c: string) => CLINIC_COLORS[Math.max(0, clinics.indexOf(c)) % CLINIC_COLORS.length];

  // Respect the clinic filter (client-side) for both cards and bars.
  const segs = (stage: string) => byClinic.filter((x) => x.stage === stage && (!clinicFilter || x.clinic === clinicFilter));
  const stageTotal = (stage: string) => segs(stage).reduce((a, x) => a + x.count, 0);
  const stages: Stage[] = allStages.map((s) => ({ ...s, count: clinicFilter ? stageTotal(s.key) : s.count }));
  const total = stages.reduce((a, s) => a + s.count, 0);
  // Bottom bars exclude "done"; the 3 active stages are normalized so their sum = 100%.
  const activeStages = stages.filter((s) => s.key !== 'done');
  const activeTotal = activeStages.reduce((a, s) => a + s.count, 0);

  return (
    <div dir="rtl">
      <div className="pat-unified"><div className="pat-unified-section">
        <div className="pat-panel-head" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span className="pat-panel-title">תמונת מצב · כמה מטופלים בכל שלב</span>
          <span className="pat-panel-sub" style={{ margin: 0 }}>נכון לנקודת-הזמן הנבחרת. "סיים" מדויק להיום (status=Finish); היסטורי לפי תאריך-סיום (~19% מהסיומים בלי תאריך — נופלים לשלב האחרון).</span>
        </div>
      </div></div>

      <div className="pat-filter-bar">
        <label className="pat-filter">
          <span className="pat-filter-label">נקודת-זמן (סוף התקופה)</span>
          <select className="pat-cohort-period-select" value={asOf} onChange={(e) => setAsOf(e.target.value)}>
            {dateOpts.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </label>
        <label className="pat-filter">
          <span className="pat-filter-label">קופה</span>
          <select className="pat-cohort-period-select" value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)}>
            <option value="">כל הקופות</option>
            {clinics.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span style={{ alignSelf: 'flex-end', fontSize: 12, color: 'var(--ex-text-muted)' }}>{loading ? 'טוען…' : `סה״כ ${fmt(total)} · נכון ל-${asOf}`}</span>
      </div>

      {/* 4 lifecycle cards (פרי/חדש/פעיל clickable to filter the bars; סיים = number only) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
        {stages.map((s) => {
          const clickable = s.key !== 'done';
          const sel = selStage === s.key;
          return (
            <div key={s.key}
              onClick={clickable ? () => setSelStage(sel ? null : s.key) : undefined}
              style={{ background: 'var(--ex-bg-soft)', border: `1px solid ${sel ? STAGE_COLOR[s.key] : 'var(--ex-border)'}`, borderRadius: 12, padding: '14px 16px', borderTop: `3px solid ${STAGE_COLOR[s.key]}`, cursor: clickable ? 'pointer' : 'default', boxShadow: sel ? `0 0 0 1px ${STAGE_COLOR[s.key]}` : 'none' }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--ex-text-primary)', letterSpacing: '-0.5px', fontFeatureSettings: "'tnum'" }}>{fmt(s.count)}</div>
              <div style={{ fontSize: 13, color: 'var(--ex-text-secondary)', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: 'var(--ex-text-muted)', marginTop: 2 }}>
                {clickable
                  ? `${activeTotal ? Math.round((s.count / activeTotal) * 100) : 0}% מהפעילים · לחץ לפילוח`
                  : `${total ? Math.round((s.count / total) * 100) : 0}% מהסה״כ`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bars — exclude "done"; the 3 active stages normalized so their sum = 100% */}
      <div className="pat-unified"><div className="pat-unified-section">
        <div className="pat-panel-head">
          <span className="pat-panel-title">
            {selStage ? `${stages.find((s) => s.key === selStage)?.label} · לפי קופה` : 'התפלגות הפעילים (בלי סיום) · יחס מ-100%'}
            {selStage && <button onClick={() => setSelStage(null)} style={{ marginInlineStart: 10, background: 'none', border: 'none', color: 'var(--ex-blue-text)', cursor: 'pointer', fontSize: 12 }}>בטל פילוח ✕</button>}
          </span>
          {!clinicFilter && !selStage && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {clinics.map((c) => (
                <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ex-text-muted)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: clinicColor(c) }} />{c}
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {selStage ? (
            segs(selStage).sort((a, b) => b.count - a.count).map((x) => {
              const stageTot = stageTotal(selStage);
              return (
                <div key={x.clinic} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 160, fontSize: 13, color: 'var(--ex-text-secondary)' }}>{x.clinic}</span>
                  <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                    <div style={{ width: `${stageTot ? (x.count / stageTot) * 100 : 0}%`, height: '100%', background: clinicColor(x.clinic) }} />
                  </div>
                  <span style={{ width: 110, textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'var(--ex-text-primary)' }}>{fmt(x.count)} · {stageTot ? Math.round((x.count / stageTot) * 100) : 0}%</span>
                </div>
              );
            })
          ) : (
            activeStages.map((s) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 200, fontSize: 13, color: 'var(--ex-text-secondary)' }}>{s.label}</span>
                <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', width: `${activeTotal ? (s.count / activeTotal) * 100 : 0}%`, height: '100%' }}>
                    {segs(s.key).sort((a, b) => b.count - a.count).map((x) => (
                      <div key={x.clinic} title={`${x.clinic}: ${fmt(x.count)}`} style={{ width: `${s.count ? (x.count / s.count) * 100 : 0}%`, height: '100%', background: clinicColor(x.clinic) }} />
                    ))}
                  </div>
                </div>
                <span style={{ width: 110, textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'var(--ex-text-primary)' }}>{fmt(s.count)} · {activeTotal ? Math.round((s.count / activeTotal) * 100) : 0}%</span>
              </div>
            ))
          )}
        </div>
      </div></div>
    </div>
  );
}
